import React from "react";
import { useNavigate } from "react-router-dom";
import ReusableMenu from "../menu/ReusableMenu";
import downArrow from "../../assets/down-arrow.svg";
import rightChev from "../../assets/rightchev.svg";
import loader from "../../assets/button-loader.svg";
import { Tooltip } from "@mui/material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";


const SubscriptionListTable = ({
  theads,
  allWallets = [],
  prevPage,
  nextPage,
  paginate,
  currentPage,
  indexOfFirstItem,
  indexOfLastItem,
  itemsPerPage,
  isFetching,
  handleItemsPerPageChange,
  totalCount,
}) => {
  const navigate = useNavigate();

  const getStatusLabel = (status) => {
    if (status === 'ACTIVE') return 'Active';
    if (status === 'APPROVAL_PENDING') return 'Pending';
    return 'Inactive';
  };

  const handleWalletClick = (wallet_address, item) => {
    console.log('Navigating to wallet details for:', wallet_address);
    navigate(`/subscription/wallet/${wallet_address}`, { state: { subscription: item } });
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const tableColumn = theads.map((h) => h.name);
    const tableRows = allWallets.map((item) => [
      item.wallet_address,
      item.email,
      item.amount ? `${item.amount}$` : "",
      item.next_payment_date,
      item.created_at,
      getStatusLabel(item.subscription_status)
    ]);

    doc.setFontSize(18);
    doc.text("Subscription List", 14, 18);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 24,
      styles: {
        fontSize: 10,
        cellPadding: 3,
        valign: 'middle',
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 45 },
        2: { cellWidth: 20, halign: 'right' },
        3: { cellWidth: 32, halign: 'center' },
        4: { cellWidth: 32, halign: 'center' },
        5: { cellWidth: 38, halign: 'center' },
      },
      bodyStyles: {
        halign: 'left',
      },
      didParseCell: function (data) {
        if (data.column.index === 2) {
          data.cell.styles.halign = 'right';
        }
        if ([3, 4, 5].includes(data.column.index)) {
          data.cell.styles.halign = 'center';
        }
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });
    doc.save("subscription-list.pdf");
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <button
          className="bg-[#353535] hover:bg-[#232323] text-[#D9D9D9] font-semibold py-2 px-6 rounded-lg border border-[#313D4F] shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleExportPDF}
          disabled={isFetching || allWallets.length === 0}
        >
          Export PDF
        </button>
      </div>
      <div className="rounded-xl bg-[#1E1E1E] shadow-lg border border-[#313D4F]">
        <div className="inline-block w-full rounded-lg overflow-auto">
          <div
            className={`${!isFetching ? "h-[60vh] " : ""} ${
              allWallets.length > 5 && !isFetching ? " h-[70vh]" : ""
            } overflow-clip rounded-xl flex flex-col`}>
            <table className="w-full table-fixed border-separate border-spacing-0">
              <thead className="text-[#D9D9D9] bg-[#353535] sticky top-0 z-10">
                <tr className="border-b border-[#313D4F]">
                  <th className="w-[25%] px-4 py-4 text-left text-sm font-semibold tracking-wider whitespace-nowrap">Wallet Address</th>
                  <th className="w-[20%] px-4 py-4 text-left text-sm font-semibold tracking-wider whitespace-nowrap">Email</th>
                  <th className="w-[12%] px-4 py-4 text-right text-sm font-semibold tracking-wider whitespace-nowrap pr-6">Amount</th>
                  <th className="w-[15%] py-4 text-center text-sm font-semibold tracking-wider whitespace-nowrap px-0">Next Payment</th>
                  <th className="w-[15%] py-4 text-center text-sm font-semibold tracking-wider whitespace-nowrap px-0">Created At</th>
                  <th className="w-[13%] py-4 text-center text-sm font-semibold">Status</th>
                </tr>
              </thead>
            </table>
            {!isFetching && (
              <>
                {allWallets?.length > 0 && (
                  <div className="flex-1 overflow-y-auto">
                    <table className="w-full table-fixed">
                      <tbody className="text-[#F3F3F3] bg-[#1E1E1E] divide-y divide-[#313D4F]">
                        {allWallets?.map((item, ind) => (
                          <tr key={`${item?.wallet_address}=${ind}`} className="hover:bg-[#2A2A2A] transition-colors">
                            <td className="w-[25%] px-4 py-4">
                              <Tooltip
                                placement="top-start"
                                className="cursor-pointer"
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      border: "1px solid #313D4F",
                                      fontSize: "12px",
                                      bgcolor: "#1E1E1E",
                                      "& .MuiTooltip-arrow": {
                                        color: "#313D4F",
                                      },
                                    },
                                  },
                                }}
                                arrow
                                title={item?.wallet_address?.length > 30 ? item?.wallet_address : null}>
                                <button
                                  className="text-sm font-medium truncate text-left w-full hover:underline focus:outline-none"
                                  style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer' }}
                                  onClick={() => handleWalletClick(item?.wallet_address, item)}
                                >
                                  {item?.wallet_address?.length > 30
                                    ? `${item?.wallet_address?.slice(0, 30)}...`
                                    : item?.wallet_address}
                                </button>
                              </Tooltip>
                            </td>
                            <td className="w-[20%] px-4 py-4">
                              <p className="text-sm truncate">{item?.email}</p>
                            </td>
                            <td className="w-[12%] px-4 py-4 text-right pr-6">
                              <p className="text-sm font-medium whitespace-nowrap">
                                {item?.amount ? `${item?.amount}$` : ""}
                              </p>
                            </td>
                            <td className="w-[15%] py-4 text-center px-0">
                              <p className="text-sm whitespace-nowrap">{item?.next_payment_date}</p>
                            </td>
                            <td className="w-[15%] py-4 text-center px-0">
                              <p className="text-sm whitespace-nowrap">{item?.created_at}</p>
                            </td>
                            <td className="w-[13%] py-4 text-center px-0">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${item?.subscription_status === 'ACTIVE' ? 'bg-green-900/50 text-green-400' : 'bg-yellow-900/50 text-yellow-400'}`}>
                                {getStatusLabel(item?.subscription_status)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
          {allWallets?.length <= 0 && !isFetching && (
            <div className="flex text-[#D9D9D9] min-h-[25vh] justify-center items-center">
              No Data Found
            </div>
          )}
          {isFetching && (
            <div className="flex min-h-[50vh] justify-center items-center">
              <img src={loader} alt="loader" className="w-10 h-10" />
            </div>
          )}
        </div>
      </div>
      {allWallets?.length > 0 && (
        <div className="flex justify-between items-center mt-2">
          <div className="opacity-60 text-zinc-100 text-sm font-semibold">
            Showing {indexOfFirstItem + 1}-
            {Math.min(indexOfLastItem, totalCount)} of {totalCount} entries
          </div>
          <div className="flex mt-4 justify-center items-center">
            <div className="justify-start items-center gap-2.5 inline-flex pr-3">
              <div className="text-zinc-100 text-sm font-semibold">
                Items per page
              </div>
              <div className="w-[70px] flex-col justify-start items-start gap-2 inline-flex">
                <div className="relative inline-block text-left">
                  <ReusableMenu
                    buttonIcon={downArrow}
                    isFetching={isFetching}
                    menuItems={[20, 35, 50, "See All"]}
                    buttonText={itemsPerPage}
                    handleChangeFun={handleItemsPerPageChange}
                  />
                </div>
              </div>
            </div>
            <div className="w-[157px] h-[42px] flex-col justify-start items-start gap-[30px] inline-flex">
              <div className="justify-start items-center gap-2 inline-flex">
                <div className="text-zinc-100 text-sm font-semibold">Page</div>
                <div className="w-[70px] flex-col justify-start items-start gap-2 inline-flex">
                  <div className="relative inline-block text-left">
                    <ReusableMenu
                      buttonIcon={downArrow}
                      menuItems={Array.from(
                        {
                          length: Math.ceil(totalCount / itemsPerPage),
                        },
                        (_, i) => i + 1
                      )}
                      buttonText={currentPage}
                      handleChangeFun={paginate}
                      isFetching={isFetching}
                    />
                  </div>
                </div>
                <div className="text-zinc-100 text-sm font-semibold">
                  of {Math.ceil(totalCount / itemsPerPage)}
                </div>
              </div>
            </div>
            <div className="w-[86px] h-[30px] ml-2 relative">
              <div className="w-[86px] h-[30px] p-2 justify-center items-center flex bg-[#1E1E1E] rounded-[27px] border border-[#787878]" />
              <button disabled={isFetching} onClick={prevPage}>
                <img
                  src={rightChev}
                  alt="right"
                  className="w-auto h-[15px] rotate-180 left-[10px] top-[7px] absolute cursor-pointer"
                />
              </button>
              <button disabled={isFetching} onClick={nextPage}>
                <img
                  src={rightChev}
                  alt="left"
                  className="rotate-270 opacity-90 w-auto h-[15px] left-[55px] top-[7px] absolute cursor-pointer"
                />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionListTable;
